import {
    getPositionTableForAccountAndSymbol,
    getPositionTableForAccountsAndSymbols,
    getTickersTableForSymbol,
    getTickersTableForSymbols,
} from './http/tables';
import type { PositionTableForAccountAndSymbol } from './http/tables';
import type { QueryObserverOptions, QueryClient } from '@tanstack/query-core';
import type { ColumnOptions, OutputColumn } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ScreenerPlanDTO } from 'src/features/screener/dtos/ScreenerPlanDTO';
import { ideaDTOToStage } from 'src/features/table/lib/ideaStages';
import { createTable } from 'src/lib/table';
import { FilterStage, OutputStage, SourceStage } from 'src/lib/tableStages';
import type { Stage } from 'src/lib/tableStages';
import type { SlugMapService } from 'src/services/SlugMapService';
import type { FetchableTableCreationResultWithColumns } from 'src/types';

const TABLE = 'table';
const ROW_COUNT = 'row-count';
const ALL_TICKERS = 'all-tickers';
const FILTERED = 'filtered';
const TICKER_FEED_TABLE = 'ticker-feed-table';
const POSITION_FEED_TABLE = 'position-feed-table';

/**
 * Escape a value for safe interpolation inside a single-quoted formula string
 * literal. Backslashes must be escaped first so that the subsequent
 * single-quote escaping doesn't introduce unescaped backslashes.
 */
function escapeFormulaStringLiteral(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");
}

export const getTotalRowsCountQuery = (
    universeId?: string,
    columnOptions?: Partial<ColumnOptions>,
): QueryObserverOptions<FetchableTableCreationResultWithColumns> => {
    return {
        queryKey: [TABLE, ROW_COUNT, ALL_TICKERS, universeId, ...(columnOptions ? [{ columnOptions }] : [])],
        queryFn: async () => {
            const columns: OutputColumn[] = [];
            let stage: Stage = universeId
                ? SourceStage.fromTickerUniverseId(universeId)
                : SourceStage.allForKey('ticker');
            stage = OutputStage.fromInputStage(stage, {}, columns);

            return {
                ...(await createTable({
                    leafStage: stage.build(),
                    ...(columnOptions && { columnOptionContext: columnOptions }),
                })),
                fetchMode: 'snapshot',
            };
        },
        staleTime: 0,
        gcTime: 0,
    };
};

/**
 * Currently Screener specific, we will need to handle the possible null from plan.toStage to use with scanner
 */
export const getFilteredRowsCountQuery = (
    plan: ScreenerPlanDTO,
    _queryClient: QueryClient,
    userViewKey: string,
    columnOptions?: Partial<ColumnOptions>,
): QueryObserverOptions<FetchableTableCreationResultWithColumns> => {
    return {
        queryKey: [
            TABLE,
            ROW_COUNT,
            FILTERED,
            plan.toContract(),
            userViewKey,
            ...(columnOptions ? [{ columnOptions }] : []),
        ],
        queryFn: async () => {
            const stage = ideaDTOToStage(SourceStage.allForKey('ticker'), plan.toTempIdea()).build();
            return {
                ...(await createTable({
                    leafStage: stage,
                    userViewKey,
                    ...(columnOptions && { columnOptionContext: columnOptions }),
                })),
                fetchMode: 'snapshot',
            };
        },
        staleTime: 0,
        gcTime: 0,
    };
};

export const getTickersForSymbolQuery = (
    symbol?: string,
): QueryObserverOptions<FetchableTableCreationResultWithColumns | null> => {
    return {
        queryKey: [TABLE, TICKER_FEED_TABLE, symbol],
        queryFn: () => {
            if (!symbol) {
                return null;
            }
            return getTickersTableForSymbol({ symbol });
        },
        staleTime: 0,
        gcTime: 0,
    };
};

export const getTickersForSymbolsQuery = (
    symbols: string[],
): QueryObserverOptions<FetchableTableCreationResultWithColumns> => {
    return {
        queryKey: [TABLE, TICKER_FEED_TABLE, 'multi', ...symbols.slice().sort()],
        queryFn: () => getTickersTableForSymbols({ symbols }),
        staleTime: 0,
        gcTime: 0,
        enabled: symbols.length > 0,
    };
};

export const getPositionForAccountAndSymbolQuery = ({
    accountId,
    symbol,
}: {
    accountId?: string;
    symbol?: string;
} = {}): QueryObserverOptions<PositionTableForAccountAndSymbol | null> => {
    return {
        queryKey: [TABLE, POSITION_FEED_TABLE, accountId, symbol],
        queryFn: async () => {
            if (!symbol || !accountId) {
                return null;
            }
            return await getPositionTableForAccountAndSymbol({ accountId, symbol });
        },
        staleTime: 0,
        gcTime: 0,
    };
};

export const getPositionsForAccountsAndSymbolsQuery = ({
    accountIds,
    symbols,
}: {
    accountIds: string[];
    symbols: string[];
}): QueryObserverOptions<PositionTableForAccountAndSymbol> => {
    return {
        queryKey: [
            TABLE,
            POSITION_FEED_TABLE,
            'bulk',
            'accounts',
            ...accountIds.slice().sort(),
            'symbols',
            ...symbols.slice().sort(),
        ],
        queryFn: () => getPositionTableForAccountsAndSymbols({ accountIds, symbols }),
        staleTime: 0,
        gcTime: 0,
        enabled: accountIds.length > 0 && symbols.length > 0,
    };
};

export function getToolbarTickerItemQuery(options: {
    toolbarItemKey: string;
    symbol: string;
    slugMapServiceProvider: () => Promise<SlugMapService>;
    outputColumns: OutputColumn[];
}): QueryObserverOptions<FetchableTableCreationResultWithColumns> {
    const { toolbarItemKey, symbol, slugMapServiceProvider, outputColumns } = options;
    return {
        queryKey: ['toolbar-item', toolbarItemKey],
        queryFn: async (): Promise<FetchableTableCreationResultWithColumns> => {
            let stage: Stage = SourceStage.allForKey('ticker');

            const slugMapService = await slugMapServiceProvider();
            const symbolIndicator = slugMapService.slugMap.value.symbol;
            stage = FilterStage.fromInputStage(stage, {
                type: 'filter',
                formula: `${symbolIndicator.symbol}=='${escapeFormulaStringLiteral(symbol)}'`,
                imports: [{ id: symbolIndicator.id, alias: symbolIndicator.symbol }],
            });

            stage = OutputStage.fromInputStage(stage, {}, outputColumns);

            const result = await createTable({
                leafStage: stage.build(),
            });

            return {
                ...result,
                fetchMode: 'snapshot',
            };
        },
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
    };
}
